import { sortBanquets } from "./banquetSorting.js";
import { calculateDistanceKm } from "./calculateDistance.js";
import { getPincodeCoordinates } from "./pincodeCoordinates.js";

const normalizeText = (value) => String(value || "").trim().toLowerCase();
const contains = (value, query) => normalizeText(value).includes(normalizeText(query));
const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};
const hasTextMatch = (items, query) => toArray(items).some((item) => contains(item, query));

const matchesEventType = (banquet, eventType) =>
  !eventType || (banquet.eventTypes || []).some((item) => item.toLowerCase() === eventType.toLowerCase());

const matchesVenueStyle = (banquet, style) => !style || (banquet.indoorOutdoor || []).includes(style);

export const getBanquetLocationText = (banquet) =>
  [
    banquet.area,
    banquet.locality,
    banquet.city,
    banquet.address,
    banquet.locationDetails?.locality,
    banquet.locationDetails?.city,
    banquet.locationDetails?.fullAddress,
  ]
    .filter(Boolean)
    .join(" ");

export const getBanquetVenueTypes = (banquet) => {
  const venueTypes = [
    ...toArray(banquet.basicDetails?.venueTypes),
    banquet.venueType,
    ...toArray(banquet.venueTypes),
    ...toArray(banquet.indoorOutdoor),
  ];

  return [...new Set(venueTypes.filter(Boolean))];
};

export const getBanquetEventTypes = (banquet) => [...new Set(toArray(banquet.eventTypes).filter(Boolean))];

export const getBanquetMinimumGuestRange = (banquet) => {
  const explicitRange = banquet.capacityDetails?.minimumGuestRange || banquet.minimumGuestRange;
  if (explicitRange) {
    return String(explicitRange).replace(/\s+/g, "");
  }

  const hallMinimums = toArray(banquet.capacityDetails?.halls)
    .map((hall) => toNumber(hall.minimumGuests))
    .filter((value) => value !== null);
  const minimumGuests = hallMinimums.length ? Math.min(...hallMinimums) : toNumber(banquet.guestMin);

  if (!minimumGuests) {
    return "";
  }

  const matchingRange = guestRangeOptions.find(({ min, max }) => minimumGuests >= min && minimumGuests <= max);
  return matchingRange?.value || `${minimumGuests}+`;
};

const getMinimumGuestValue = (banquet) => {
  const range = getBanquetMinimumGuestRange(banquet);
  const rangeStart = range.match(/^(\d+)/)?.[1];
  const hallMinimums = toArray(banquet.capacityDetails?.halls)
    .map((hall) => toNumber(hall.minimumGuests))
    .filter((value) => value !== null);

  return toNumber(rangeStart) ?? (hallMinimums.length ? Math.min(...hallMinimums) : toNumber(banquet.guestMin));
};

export const getBanquetVegPrice = (banquet) =>
  toNumber(banquet.pricingDetails?.vegStartingFrom) ?? toNumber(banquet.vegPrice) ?? toNumber(banquet.pricing?.vegPrice);

export const getBanquetNonVegPrice = (banquet) =>
  toNumber(banquet.pricingDetails?.nonVegStartingFrom) ?? toNumber(banquet.nonVegPrice) ?? toNumber(banquet.pricing?.nonVegPrice);

export const hasNonVegFood = (banquet) => {
  const foodText = normalizeText(
    [
      banquet.pricingDetails?.foodType,
      banquet.foodType,
      banquet.foodCatering?.foodType,
      toArray(banquet.foodCatering?.specialFoodServices).join(" "),
    ].join(" ")
  );

  return Boolean(getBanquetNonVegPrice(banquet)) || foodText.includes("non");
};

export const hasVegFood = (banquet) => Boolean(getBanquetVegPrice(banquet)) || normalizeText(banquet.foodType).includes("veg");

export const isAlcoholAllowed = (banquet) => {
  if (typeof banquet.alcoholPolicy?.alcoholAllowed === "boolean") {
    return banquet.alcoholPolicy.alcoholAllowed;
  }

  if (typeof banquet.liquorAllowed === "boolean") {
    return banquet.liquorAllowed;
  }

  if (hasTextMatch(banquet.facilities, "Alcohol Allowed") || hasTextMatch(banquet.facilities, "Liquor Allowed")) {
    return true;
  }

  if (hasTextMatch(banquet.facilities, "No Alcohol") || hasTextMatch(banquet.facilities, "Alcohol Not Allowed")) {
    return false;
  }

  return null;
};

export const isParkingAvailable = (banquet) => {
  // parkingDetails (the dedicated Parking section) and the derived top-level field are the
  // consolidated single source of truth — checked first so they can't be masked by the
  // legacy capacityDetails.parkingAvailable default, which normalizeBanquet always resolves
  // to a boolean and would otherwise short-circuit this check even when empty/unset.
  if (typeof banquet.parkingDetails?.parkingAvailable === "boolean") {
    return banquet.parkingDetails.parkingAvailable;
  }

  if (typeof banquet.parkingAvailable === "boolean") {
    return banquet.parkingAvailable;
  }

  if (typeof banquet.capacityDetails?.parkingAvailable === "boolean") {
    return banquet.capacityDetails.parkingAvailable;
  }

  if (hasTextMatch(banquet.facilities, "Parking Available")) {
    return true;
  }

  return null;
};

export const getParkingCapacity = (banquet) =>
  toNumber(banquet.parkingDetails?.totalParkingCapacity) ??
  toNumber(banquet.capacityDetails?.parkingCapacity) ??
  toNumber(banquet.parkingCapacity);

export const hasValetParking = (banquet) =>
  Boolean(banquet.valetParking) ||
  hasTextMatch(banquet.capacityDetails?.parkingTypes, "Valet") ||
  hasTextMatch(banquet.parkingDetails?.parkingTypes, "Valet") ||
  hasTextMatch(banquet.facilities, "Valet Parking");

const matchesFoodPreference = (banquet, foodPreference) => {
  if (!foodPreference) {
    return true;
  }

  if (foodPreference === "veg-only") {
    return hasVegFood(banquet) && !hasNonVegFood(banquet);
  }

  if (foodPreference === "non-veg") {
    return hasNonVegFood(banquet);
  }

  if (foodPreference === "veg-non-veg") {
    return hasVegFood(banquet) && hasNonVegFood(banquet);
  }

  return true;
};

const getPriceTier = (banquet) => {
  const startingPrice = Math.min(
    getBanquetVegPrice(banquet) ?? Number.POSITIVE_INFINITY,
    getBanquetNonVegPrice(banquet) ?? Number.POSITIVE_INFINITY
  );

  if (startingPrice < 1000) {
    return "under-1000";
  }

  if (startingPrice >= 1000 && startingPrice <= 1500) {
    return "1000-1500";
  }

  if (startingPrice > 1500 && startingPrice <= 2000) {
    return "1500-2000";
  }

  if (startingPrice > 2000) {
    return "2000-plus";
  }

  return "";
};

const isPremiumPriceTier = (tier) => tier === "2000-plus";

const matchesQuickChip = (banquet, chip, resolvedPincode) => {
  switch (chip) {
    case "Near Me":
      return Boolean(resolvedPincode) && typeof banquet.distanceKm === "number";
    case "100+ Guests":
      return banquet.guestMax >= 100;
    case "200+ Guests":
      return banquet.guestMax >= 200;
    case "Budget Friendly":
      return getPriceTier(banquet) === "under-1000" || getPriceTier(banquet) === "1000-1500";
    case "Premium":
      return isPremiumPriceTier(getPriceTier(banquet));
    case "Liquor Allowed":
      return isAlcoholAllowed(banquet) === true;
    case "Good Parking":
      return isParkingAvailable(banquet) === true && (getParkingCapacity(banquet) || 0) >= 40;
    case "Lawn":
      return (banquet.indoorOutdoor || []).includes("lawn");
    case "AC Hall":
      return banquet.acHall;
    default:
      return true;
  }
};

const priceInRange = (price, minPrice, maxPrice) => {
  if (minPrice && price < Number(minPrice)) {
    return false;
  }

  if (maxPrice && price > Number(maxPrice)) {
    return false;
  }

  return true;
};

const guestThresholds = {
  "50+": 50,
  "100+": 100,
  "150+": 150,
  "250+": 250,
  "400+": 400,
  "600+": 600,
};

export const guestRangeOptions = [
  { value: "25-50", label: "25-50", min: 25, max: 50 },
  { value: "50-75", label: "50-75", min: 50, max: 75 },
  { value: "75-100", label: "75-100", min: 75, max: 100 },
  { value: "100-125", label: "100-125", min: 100, max: 125 },
  { value: "125-150", label: "125-150", min: 125, max: 150 },
  { value: "150-200", label: "150-200", min: 150, max: 200 },
  { value: "200-300", label: "200-300", min: 200, max: 300 },
  { value: "300-500", label: "300-500", min: 300, max: 500 },
];

const capacityThresholds = {
  "50+": 50,
  "100+": 100,
  "150+": 150,
  "250+": 250,
  "400+": 400,
  "500+": 500,
};

const getCapacityValue = (banquet) =>
  Math.max(
    toNumber(banquet.capacityDetails?.seatingCapacity) ?? 0,
    toNumber(banquet.capacityDetails?.floatingCapacity) ?? 0,
    toNumber(banquet.guestMax) ?? 0
  );

const matchesMinimumGuestRange = (banquet, selectedRange) => {
  if (!selectedRange) {
    return true;
  }

  const option = guestRangeOptions.find((item) => item.value === selectedRange);
  const minimumGuestValue = getMinimumGuestValue(banquet);

  if (!option || minimumGuestValue === null) {
    return false;
  }

  return minimumGuestValue >= option.min && minimumGuestValue <= option.max;
};

const matchesPriceFilter = (banquet, priceFilter) => {
  if (!priceFilter) {
    return true;
  }

  if (priceFilter === "hall-rental") {
    return Boolean(toNumber(banquet.pricingDetails?.hallRentalStartingFrom));
  }

  return getPriceTier(banquet) === priceFilter;
};

const matchesAvailability = (banquet, availabilityFilter) => {
  if (!availabilityFilter) {
    return true;
  }

  const calendar = banquet.availabilityCalendar || {};
  const dates = calendar.dates || {};
  const bookedSlots = calendar.bookedSlots || banquet.bookedSlots || {};
  const hasCalendarData = Object.keys(dates).length > 0 || Object.keys(bookedSlots).length > 0;

  if (!hasCalendarData) {
    return true;
  }

  const dateKey = (offsetDays) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + offsetDays);
    return date.toISOString().split("T")[0];
  };
  const isDateAvailable = (key) => dates[key] ? dates[key] === "available" : !bookedSlots[key]?.lunch || !bookedSlots[key]?.dinner;
  const todayKey = dateKey(0);

  if (availabilityFilter === "today") {
    return isDateAvailable(todayKey);
  }

  if (availabilityFilter === "weekend") {
    const now = new Date();
    const daysUntilSaturday = (6 - now.getDay() + 7) % 7;
    const saturdayKey = dateKey(daysUntilSaturday);
    const sundayKey = dateKey(daysUntilSaturday + 1);
    return isDateAvailable(saturdayKey) || isDateAvailable(sundayKey);
  }

  if (availabilityFilter === "lunch") {
    return !bookedSlots[todayKey]?.lunch;
  }

  if (availabilityFilter === "dinner") {
    return !bookedSlots[todayKey]?.dinner;
  }

  return true;
};

export const matchesEventDateSlot = (banquet, eventDate, eventSlot) => {
  if (!eventDate) {
    return true;
  }

  const dates = banquet.availabilityCalendar?.dates || {};
  const status = dates[eventDate] || "available";
  const slot = eventSlot || "fullday";

  if (status === "blocked") {
    return false;
  }

  if (slot === "lunch") {
    return status !== "lunchBooked" && status !== "fullDayBooked";
  }

  if (slot === "dinner") {
    return status !== "dinnerBooked" && status !== "fullDayBooked";
  }

  return status === "available";
};

export const getBanquetNameScore = (banquet, nameQuery) => {
  const query = normalizeText(nameQuery);
  if (!query) {
    return 0;
  }

  const name = normalizeText(banquet.name);
  const pincodeDigits = String(banquet.pincode || "").trim();
  const queryDigits = query.replace(/\D/g, "");

  if (name === query) {
    return 4;
  }

  if (name.startsWith(query)) {
    return 3;
  }

  if (name.includes(query)) {
    return 2;
  }

  if (queryDigits && pincodeDigits && pincodeDigits.includes(queryDigits)) {
    return 1;
  }

  return contains(getBanquetLocationText(banquet), query) ? 1 : 0;
};

const getAreaScore = (banquet, areaQuery) => {
  if (!areaQuery) {
    return 0;
  }

  const query = normalizeText(areaQuery);
  const locality = normalizeText(banquet.locality || banquet.locationDetails?.locality);
  const city = normalizeText(banquet.city || banquet.locationDetails?.city);
  const locationText = normalizeText(getBanquetLocationText(banquet));

  if (locality === query || city === query) {
    return 3;
  }

  if (locality.includes(query) || city.includes(query)) {
    return 2;
  }

  return locationText.includes(query) ? 1 : 0;
};

const relatedAreaTokens = {
  vasundhara: ["ghaziabad", "indirapuram", "raj nagar extension", "vaishali"],
  indirapuram: ["ghaziabad", "vasundhara", "vaishali", "noida"],
  "raj nagar extension": ["ghaziabad", "vasundhara", "indirapuram"],
  ghaziabad: ["vasundhara", "indirapuram", "raj nagar extension", "vaishali"],
  noida: ["indirapuram", "ghaziabad", "sector"],
};

const isRelatedArea = (banquet, areaQuery) => {
  const query = normalizeText(areaQuery);
  const locationText = normalizeText(getBanquetLocationText(banquet));
  const relatedTokens = relatedAreaTokens[query] || [];

  return relatedTokens.some((token) => locationText.includes(token)) || locationText.includes(query.split(" ")[0]);
};

const getDefaultSort = (filters, resolvedPincode, userLocation) => {
  if (userLocation) {
    return "nearest";
  }

  if (filters.sortBy) {
    return filters.sortBy;
  }

  if (resolvedPincode && filters.distanceRadius) {
    return "nearest";
  }

  return "featured";
};

export const defaultBanquetFilters = {
  name: "",
  area: "",
  city: "",
  locality: "",
  pincode: "",
  distanceRadius: "10",
  guestTier: "",
  minimumGuestRange: "",
  minGuests: "",
  maxGuests: "",
  priceTier: "",
  budget: "",
  vegPriceMin: "",
  vegPriceMax: "",
  nonVegPriceMin: "",
  nonVegPriceMax: "",
  foodPreference: "",
  foodType: "",
  liquor: "",
  alcohol: "",
  parkingStatus: "",
  parkingCapacity: "",
  byobAllowed: false,
  parkingAvailable: false,
  valetParking: false,
  roomsAvailable: false,
  bridalRoom: false,
  changingRoom: false,
  venueStyle: "",
  acHall: false,
  powerBackup: false,
  lift: false,
  wheelchairFriendly: false,
  decorPolicy: "",
  cateringPolicy: "",
  djAllowed: false,
  lateNightAllowed: false,
  eventType: "",
  venueTypes: [],
  eventTypes: [],
  capacityTier: "",
  availability: "",
  eventDate: "",
  eventSlot: "fullday",
  sortBy: "",
};

export const getResolvedPincode = (pincode) => getPincodeCoordinates(pincode.trim());

export const buildBanquetResults = (banquets, filters, quickChips, userLocation) => {
  const resolvedPincode = getResolvedPincode(filters.pincode);
  const distanceRadius = Number(filters.distanceRadius || 0);

  const enrichedBanquets = banquets.map((banquet) => {
    const distanceOrigin = userLocation || resolvedPincode;
    const distanceKm = distanceOrigin
      ? calculateDistanceKm(distanceOrigin, { latitude: banquet.latitude, longitude: banquet.longitude })
      : null;

    return {
      ...banquet,
      distanceKm,
      areaScore: getAreaScore(banquet, filters.area),
      nameScore: getBanquetNameScore(banquet, filters.name),
      searchPincode: filters.pincode.trim(),
    };
  });

  const matchesNonAreaFilters = (banquet) => {
    if (filters.name && getBanquetNameScore(banquet, filters.name) === 0) {
      return false;
    }

    if (filters.city && banquet.city !== filters.city) {
      return false;
    }

    if (filters.locality && banquet.locality !== filters.locality) {
      return false;
    }

    if (resolvedPincode && distanceRadius > 0 && typeof banquet.distanceKm === "number" && banquet.distanceKm > distanceRadius) {
      return false;
    }

    if (filters.guestTier && banquet.guestMax < guestThresholds[filters.guestTier]) {
      return false;
    }

    if (filters.minGuests && banquet.guestMax < Number(filters.minGuests)) {
      return false;
    }

    if (filters.maxGuests && banquet.guestMin > Number(filters.maxGuests)) {
      return false;
    }

    if (filters.minimumGuestRange && !matchesMinimumGuestRange(banquet, filters.minimumGuestRange)) {
      return false;
    }

    if (filters.priceTier && !matchesPriceFilter(banquet, filters.priceTier)) {
      return false;
    }

    if (filters.budget && !matchesPriceFilter(banquet, filters.budget)) {
      return false;
    }

    if ((getBanquetVegPrice(banquet) || 0) > 0 && !priceInRange(getBanquetVegPrice(banquet), filters.vegPriceMin, filters.vegPriceMax)) {
      return false;
    }

    if ((getBanquetNonVegPrice(banquet) || 0) > 0 && !priceInRange(getBanquetNonVegPrice(banquet), filters.nonVegPriceMin, filters.nonVegPriceMax)) {
      return false;
    }

    if (filters.nonVegPriceMin && !getBanquetNonVegPrice(banquet)) {
      return false;
    }

    if (!matchesFoodPreference(banquet, filters.foodType || filters.foodPreference)) {
      return false;
    }

    const alcoholFilter = filters.alcohol || filters.liquor;
    if (alcoholFilter === "allowed" && isAlcoholAllowed(banquet) !== true) {
      return false;
    }

    if (alcoholFilter === "not-allowed" && isAlcoholAllowed(banquet) !== false) {
      return false;
    }

    if (filters.byobAllowed && !banquet.byobAllowed) {
      return false;
    }

    if (filters.parkingStatus === "yes" && isParkingAvailable(banquet) !== true) {
      return false;
    }

    if (filters.parkingStatus === "no" && isParkingAvailable(banquet) !== false) {
      return false;
    }

    if (filters.parkingCapacity && (getParkingCapacity(banquet) || 0) < Number(filters.parkingCapacity)) {
      return false;
    }

    if (filters.parkingAvailable && isParkingAvailable(banquet) !== true) {
      return false;
    }

    if (filters.valetParking && !hasValetParking(banquet)) {
      return false;
    }

    if (filters.roomsAvailable && !banquet.roomsAvailable) {
      return false;
    }

    if (filters.bridalRoom && !banquet.bridalRoom) {
      return false;
    }

    if (filters.changingRoom && !banquet.changingRoom) {
      return false;
    }

    if (!matchesVenueStyle(banquet, filters.venueStyle)) {
      return false;
    }

    if (filters.acHall && !banquet.acHall) {
      return false;
    }

    if (filters.powerBackup && !banquet.powerBackup) {
      return false;
    }

    if (filters.lift && !banquet.lift) {
      return false;
    }

    if (filters.wheelchairFriendly && !banquet.wheelchairFriendly) {
      return false;
    }

    if (filters.decorPolicy === "outside-allowed" && !(banquet.decorPolicy || "").toLowerCase().includes("outside decor allowed")) {
      return false;
    }

    if (filters.decorPolicy === "in-house-only" && !(banquet.decorPolicy || "").toLowerCase().includes("in-house decor only")) {
      return false;
    }

    if (filters.cateringPolicy === "outside-allowed" && !(banquet.cateringPolicy || "").toLowerCase().includes("outside catering allowed")) {
      return false;
    }

    if (filters.cateringPolicy === "in-house-only" && !(banquet.cateringPolicy || "").toLowerCase().includes("in-house catering only")) {
      return false;
    }

    if (filters.djAllowed && !banquet.djAllowed) {
      return false;
    }

    if (filters.lateNightAllowed && !banquet.lateNightAllowed) {
      return false;
    }

    if (!matchesEventType(banquet, filters.eventType)) {
      return false;
    }

    if (filters.capacityTier && getCapacityValue(banquet) < capacityThresholds[filters.capacityTier]) {
      return false;
    }

    if (filters.venueTypes?.length && !filters.venueTypes.some((venueType) => hasTextMatch(getBanquetVenueTypes(banquet), venueType))) {
      return false;
    }

    if (filters.eventTypes?.length && !filters.eventTypes.some((eventType) => hasTextMatch(getBanquetEventTypes(banquet), eventType))) {
      return false;
    }

    if (!matchesAvailability(banquet, filters.availability)) {
      return false;
    }

    if (!matchesEventDateSlot(banquet, filters.eventDate, filters.eventSlot)) {
      return false;
    }

    return quickChips.every((chip) => matchesQuickChip(banquet, chip, resolvedPincode));
  };

  const candidates = enrichedBanquets.filter(matchesNonAreaFilters);
  const exactAreaMatches = filters.area ? candidates.filter((banquet) => banquet.areaScore > 0) : candidates;
  const nearbyAreaMatches = filters.area
    ? candidates.filter((banquet) => banquet.areaScore === 0 && isRelatedArea(banquet, filters.area))
    : [];
  const shouldAddNearby = Boolean(filters.area) && exactAreaMatches.length < 3;
  const filteredBanquets = filters.area
    ? [...exactAreaMatches, ...(shouldAddNearby ? nearbyAreaMatches : [])]
    : candidates;
  const sortedResults = sortBanquets(filteredBanquets, getDefaultSort(filters, resolvedPincode, userLocation))
    .sort((left, right) => (right.areaScore || 0) - (left.areaScore || 0))
    .sort((left, right) => (right.nameScore || 0) - (left.nameScore || 0));

  return {
    resolvedPincode,
    sortBy: getDefaultSort(filters, resolvedPincode, userLocation),
    areaSearch: filters.area.trim(),
    exactAreaCount: exactAreaMatches.length,
    nearbyFallback: Boolean(filters.area) && exactAreaMatches.length === 0 && filteredBanquets.length > 0,
    mixedNearbyFallback: Boolean(filters.area) && exactAreaMatches.length > 0 && exactAreaMatches.length < filteredBanquets.length,
    results: sortedResults,
  };
};

export const filterBanquets = (banquets, filters, quickChips) => buildBanquetResults(banquets, filters, quickChips).results;
